import {
  FileSpreadsheet,
  FileText,
  FileType,
  Image,
  Mail,
  Presentation,
} from "lucide-react";
import type { Source } from "../types/cuadra";
import { Badge } from "./ui/badge";

export interface SourceCitationsProps {
  sources: Source[];
  maxVisible?: number;
}

function deduplicateSources(sources: Source[]): Source[] {
  const byFilename = new Map<string, Source>();

  for (const source of sources) {
    const existing = byFilename.get(source.filename);
    if (!existing || source.score > existing.score) {
      byFilename.set(source.filename, source);
    }
  }

  return Array.from(byFilename.values()).sort((a, b) => b.score - a.score);
}

function getFileIcon(filename: string): typeof FileText {
  const ext = filename.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "csv":
    case "xlsx":
    case "xls":
      return FileSpreadsheet;
    case "eml":
    case "msg":
      return Mail;
    case "md":
    case "txt":
      return FileType;
    case "pptx":
      return Presentation;
    default:
      return FileText;
  }
}

function getSourceIcon(source: Source): typeof FileText {
  if (source.contentType) {
    switch (source.contentType) {
      case "tabular":
        return FileSpreadsheet;
      case "email":
        return Mail;
      case "prose":
        return FileText;
      case "presentation":
        return Presentation;
      case "image_ocr":
        return Image;
      default:
        return FileText;
    }
  }
  return getFileIcon(source.filename);
}

/**
 * RAG source citations with optional per-source feedback.
 * Deduplicates by filename, showing highest relevance score per file.
 */
export function SourceCitations({
  sources,
  maxVisible = 3,
}: SourceCitationsProps) {
  if (!sources || sources.length === 0) return null;

  const uniqueSources = deduplicateSources(sources);
  const visibleSources = uniqueSources.slice(0, maxVisible);
  const hiddenCount = uniqueSources.length - maxVisible;

  return (
    <div className="cuadra-mt-4 cuadra-pt-3 cuadra-border-t cuadra-border-border">
      <div className="cuadra-flex cuadra-items-center cuadra-gap-1.5 cuadra-mb-2.5">
        <FileText className="cuadra-h-3.5 cuadra-w-3.5 cuadra-text-muted-foreground" />
        <span className="cuadra-text-xs cuadra-font-medium cuadra-text-muted-foreground cuadra-font-brand">
          Sources
        </span>
      </div>

      <div className="cuadra-flex cuadra-flex-wrap cuadra-gap-2">
        {visibleSources.map((source, index) => (
          <SourceBadge
            key={source.sourceId || index}
            source={source}
          />
        ))}
        {hiddenCount > 0 && <Badge variant="subtle">+{hiddenCount} more</Badge>}
      </div>
    </div>
  );
}

interface SourceBadgeProps {
  source: Source;
}

function SourceBadge({ source }: SourceBadgeProps) {
  const { filename, score } = source;
  const Icon = getSourceIcon(source);

  return (
    <Badge
      className="cuadra-gap-2 cuadra-cursor-default cuadra-pr-1.5"
      title={score < 1 ? `${Math.round(score * 100)}% relevant` : undefined}
    >
      <Icon className="cuadra-h-3.5 cuadra-w-3.5 cuadra-shrink-0 cuadra-opacity-60" />
      <span className="cuadra-truncate cuadra-max-w-[180px]">{filename}</span>
      {score < 1 && score > 0 && (
        <span className="cuadra-opacity-50 cuadra-text-[10px] cuadra-tabular-nums">
          {Math.round(score * 100)}%
        </span>
      )}
    </Badge>
  );
}
